import { useMemo, useState } from "react";
import { Form } from "antd";

import {
	TemplateAdministrationOffice,
	TemplateAdministrationOfficeUpsert,
	TemplateAdministrationOfficeUserUpsert
} from "../../Models";
import { IOfficeUsersAdminProps } from "../../Components/Administration/IOfficeUsersAdmin";

const useOfficeUsersAdmin = ({
	offices,
	officeUsers,
	handleSaveOffice,
	handleSaveOfficeUser
}: Pick<IOfficeUsersAdminProps, "offices" | "officeUsers" | "handleSaveOffice" | "handleSaveOfficeUser">) => {
	const [selectedInstitutionId, setSelectedInstitutionId] = useState<number | null>(null);
	const [userSearchText, setUserSearchText] = useState("");
	const [isOfficeModalOpen, setIsOfficeModalOpen] = useState(false);
	const [isOfficeUserModalOpen, setIsOfficeUserModalOpen] = useState(false);
	const [editingOffice, setEditingOffice] = useState<TemplateAdministrationOffice | null>(null);
	const [selectedOffice, setSelectedOffice] = useState<TemplateAdministrationOffice | null>(null);
	const [officeForm] = Form.useForm<TemplateAdministrationOfficeUpsert>();
	const [officeUserForm] = Form.useForm<TemplateAdministrationOfficeUserUpsert>();

	const filteredOfficeUsers = useMemo(() => {
		const normalizedSearch = userSearchText.trim().toLowerCase();

		return officeUsers.filter((officeUser) =>
			(!selectedInstitutionId || officeUser.idInstitution === selectedInstitutionId)
			&& (!selectedOffice || officeUser.idOffice === selectedOffice.id)
			&& (!normalizedSearch || officeUser.userName.toLowerCase().includes(normalizedSearch))
		);
	}, [officeUsers, selectedInstitutionId, selectedOffice, userSearchText]);

	const filteredOffices = useMemo(() => {
		const normalizedSearch = userSearchText.trim().toLowerCase();
		const matchingOfficeIds = normalizedSearch
			? new Set(
				officeUsers
					.filter((officeUser) =>
						(!selectedInstitutionId || officeUser.idInstitution === selectedInstitutionId)
						&& officeUser.userName.toLowerCase().includes(normalizedSearch)
					)
					.map((officeUser) => officeUser.idOffice)
			)
			: null;

		return offices.filter((office) =>
			(!selectedInstitutionId || office.idInstitution === selectedInstitutionId)
			&& (!matchingOfficeIds || matchingOfficeIds.has(office.id))
		);
	}, [officeUsers, offices, selectedInstitutionId, userSearchText]);

	const groupedOffices = useMemo(() => {
		const groups = filteredOffices.reduce<Record<string, {
			idInstitution: number;
			institutionName: string;
			rows: TemplateAdministrationOffice[];
		}>>((accumulator, office) => {
			const key = office.idInstitution.toString();
			if (!accumulator[key]) {
				accumulator[key] = {
					idInstitution: office.idInstitution,
					institutionName: office.institutionName,
					rows: []
				};
			}

			accumulator[key].rows.push(office);
			return accumulator;
		}, {});

		return Object.values(groups)
			.sort((a, b) => a.institutionName.localeCompare(b.institutionName))
			.map((group) => ({
				...group,
				rows: group.rows.sort((a, b) => a.description.localeCompare(b.description))
			}));
	}, [filteredOffices]);

	const openOfficeModal = (office?: TemplateAdministrationOffice) => {
		setEditingOffice(office ?? null);
		officeForm.setFieldsValue({
			id: office?.id ?? 0,
			idInstitution: office?.idInstitution ?? selectedInstitutionId ?? undefined,
			description: office?.description ?? "",
			isHeadquarter: office?.isHeadquarter ?? false
		});
		setIsOfficeModalOpen(true);
	};

	const openOfficeUsersModal = (office: TemplateAdministrationOffice) => {
		setSelectedOffice(office);
		officeUserForm.setFieldsValue({
			id: 0,
			idOffice: office.id,
			idUser: undefined
		});
		setIsOfficeUserModalOpen(true);
	};

	const handleCreateOrUpdateOffice = async () => {
		const values = await officeForm.validateFields();
		await handleSaveOffice(values);
		officeForm.resetFields();
		setIsOfficeModalOpen(false);
		setEditingOffice(null);
	};

	const handleCreateOfficeUser = async () => {
		const values = await officeUserForm.validateFields();
		await handleSaveOfficeUser(values);
		officeUserForm.resetFields();
		setIsOfficeUserModalOpen(false);
	};

	const closeOfficeModal = () => {
		setIsOfficeModalOpen(false);
		setEditingOffice(null);
	};

	const closeOfficeUsersModal = () => {
		setIsOfficeUserModalOpen(false);
		setSelectedOffice(null);
	};

	return {
		selectedInstitutionId,
		setSelectedInstitutionId,
		userSearchText,
		setUserSearchText,
		isOfficeModalOpen,
		isOfficeUserModalOpen,
		editingOffice,
		selectedOffice,
		officeForm,
		officeUserForm,
		filteredOfficeUsers,
		groupedOffices,
		openOfficeModal,
		openOfficeUsersModal,
		handleCreateOrUpdateOffice,
		handleCreateOfficeUser,
		closeOfficeModal,
		closeOfficeUsersModal
	};
};

export default useOfficeUsersAdmin;
